"use client";

import { atom, useAtomValue, useSetAtom } from "jotai";
import { useEffect } from "react";

import { accountStateAtom } from "#/store/wallet/account/accountState";
import { type UnderlyingSymbol } from "#/types/markets";

import { liquidationWarningThresholdsAtom } from "../portfolio/settings/liquidationWarningThresholds";
import { useShowLiquidationNotifications } from "../portfolio/settings/showLiquidationNotifications";
import { type LiquidationWarnings } from "./liquidationWarnings.types";
import {
  flipActiveToDismissed,
  getAccountRiskStatus,
  getDefaultLiquidationWarnings,
  getLiquidationWarningDetails,
  updateLiquidationWarningsWithAccountRiskStatus,
  updateLiquidationWarningsWithDefault,
} from "./liquidationWarnings.utils";

const accountRiskStatusAtom = atom((get) => {
  const accountState = get(accountStateAtom);
  return getAccountRiskStatus(accountState);
});

const defaultLiquidationWarningsAtom = atom((get) => {
  const liquidationWarningThresholds = get(liquidationWarningThresholdsAtom);
  return getDefaultLiquidationWarnings(liquidationWarningThresholds);
});

const liquidationWarningsAtom = atom<LiquidationWarnings>(
  {} as LiquidationWarnings,
);

const dismissLiquidationWarningAtom = atom(
  null,
  (get, set, symbol: UnderlyingSymbol) => {
    const current = get(liquidationWarningsAtom);
    const newValue = flipActiveToDismissed(current, symbol);

    set(liquidationWarningsAtom, newValue);
  },
);

const syncLiquidationWarningsAtom = atom(null, (get, set) => {
  const base = get(defaultLiquidationWarningsAtom);
  const current = get(liquidationWarningsAtom);
  const accountRiskStatus = get(accountRiskStatusAtom);

  const updated = updateLiquidationWarningsWithDefault(
    base,
    current,
    accountRiskStatus,
  );
  const final = updateLiquidationWarningsWithAccountRiskStatus(
    updated,
    accountRiskStatus,
  );

  set(liquidationWarningsAtom, final);
});

const liquidationWarningDetailsAtom = atom((get) => {
  const liquidationWarnings = get(liquidationWarningsAtom);
  const accountRiskStatus = get(accountRiskStatusAtom);

  return getLiquidationWarningDetails(liquidationWarnings, accountRiskStatus);
});

const useSyncLiquidationWarnings = () => {
  const syncLiquidationWarnings = useSetAtom(syncLiquidationWarningsAtom);
  const accountRiskStatus = useAtomValue(accountRiskStatusAtom);
  const defaultLiquidationWarnings = useAtomValue(
    defaultLiquidationWarningsAtom,
  );

  useEffect(() => {
    syncLiquidationWarnings();
  }, [syncLiquidationWarnings, accountRiskStatus, defaultLiquidationWarnings]);
};

export const useLiquidationWarnings = () => {
  useSyncLiquidationWarnings();
  const { showLiquidationNotifications } = useShowLiquidationNotifications();
  const liquidationWarningDetails = useAtomValue(liquidationWarningDetailsAtom);
  const dismissLiquidationWarning = useSetAtom(dismissLiquidationWarningAtom);

  return {
    showLiquidationNotifications,
    liquidationWarningDetails,
    dismissLiquidationWarning,
  };
};
